import { useMemo, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import { DataTableFiltersV2, useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useDataTable } from '@/hooks/use-data-table';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { FcmNotificationType } from '@/modules/notifications';
import { useFcmListRefresh } from '@/modules/notifications/hooks';
import { AuthenticatedRoutes } from '@/router/routes';

import { ClientsTable } from '../components';
import { ArchiveClientModal, DeleteClientsModal } from '../components/modals';
import {
  CLIENTS_FILTER_KEY_STATUS,
  CLIENTS_TABLE_FILTER_KEYS,
  CLIENTS_TABLE_FILTERS_STORAGE_KEY,
  getClientsTableFiltersConfig,
  getClientTableColumns,
} from '../constants';
import { useClientSummaries } from '../hooks';
import { Client, ClientModalType, ClientStatus } from '../types';

const DEFAULT_CLIENTS_FILTERS = { [CLIENTS_FILTER_KEY_STATUS]: ClientStatus.Active };

export function ClientsPage() {
  const { t } = useTranslation(['authenticated.clients', 'common']);
  const navigate = useNavigate();

  const [modalType, setModalType] = useState<ClientModalType | null>(null);

  const { isReady } = usePersistedTableFilters({
    storageKey: CLIENTS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: CLIENTS_TABLE_FILTER_KEYS,
    defaultFilters: DEFAULT_CLIENTS_FILTERS,
  });

  const filterConfig = useMemo(() => getClientsTableFiltersConfig(), []);
  const filterState = useTableFilters(filterConfig);
  const appliedFilters = filterState.activeFilters; // -> use appliedFilters for api request filtering

  const columns = useMemo(() => getClientTableColumns(), []);

  const { data, isLoading, isFetching, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useClientSummaries({
      filters: appliedFilters,
      enabled: isReady,
    });

  const { hasPendingRefresh, isRefreshing, refresh } = useFcmListRefresh({
    notificationType: FcmNotificationType.CLIENT_UPDATED,
    queryKey: ['clients'],
    isFetching,
  });

  const { table, saveColumnSizing } = useDataTable({
    data: data?.items ?? [],
    columns,
    getRowId: row => `${row.id}`,
    tableId: TableIds.CLIENTS,
    initialState: {
      columnPinning: { left: ['select', 'name'] },
    },
  });

  const selectedRows = table.getSelectedRowModel().rows.map(
    row =>
      ({
        id: row.original.id,
        name: row.original.name,
      }) as Client
  );

  const buildRowHref = (client: Client) => `${AuthenticatedRoutes.Clients}/${client.id}`;

  const handleRowClick = (client: Client) => {
    navigate(buildRowHref(client));
  };

  const handleCloseModal = () => {
    setModalType(null);
    table.resetRowSelection();
  };

  const { hasPermission } = usePermissions();
  const isCreateClientsEnabled = hasPermission(OpsiumPolicy.ClientsCreate);
  const isDeleteClientsEnabled = hasPermission(OpsiumPolicy.ClientsDelete);

  return (
    <>
      <PageTemplate
        title={t('authenticated.clients:clients')}
        useStickyHeader
        className="p-0"
        headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
        contentClassName="relative"
        rightActions={
          isCreateClientsEnabled && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => navigate(`${AuthenticatedRoutes.Clients}/new`)}>
                <CirclePlus />
                {t('authenticated.clients:addClient')}
              </Button>
            </div>
          )
        }
      >
        <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
          {hasPendingRefresh && (
            <RefreshData
              onRefresh={refresh}
              isPending={isRefreshing}
              dataUpdatedAt={null}
              description={t('authenticated.clients:refreshBanner.description')}
              buttonTitle={t('authenticated.clients:refreshBanner.buttonTitle')}
              containerClassName="mb-4"
            />
          )}
          {/* Sticky filters section */}
          <div className="mb-4 flex shrink-0 justify-between gap-2 pt-1">
            <DataTableFiltersV2
              filtersConfig={filterConfig}
              filterState={filterState}
              tableId={TableIds.CLIENTS}
              className="flex flex-wrap gap-2"
            />
            <div className="flex items-center gap-2">
              {selectedRows?.length > 0 && isDeleteClientsEnabled && (
                <>
                  {/*TODO uncomment when implement bulk archivation*/}
                  {/*{selectedRows.length === 1 && (
                  <Button size="sm" variant="outline" onClick={() => setModalType(ClientModalType.Archive)}>
                    {t('common:archive')}
                  </Button>
                )}*/}
                  <Button
                    size="sm"
                    variant="outlineDestructive"
                    onClick={() => setModalType(ClientModalType.Delete)}
                  >
                    {t('common:delete')}
                  </Button>
                </>
              )}
              {selectedRows?.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => table.resetRowSelection()}>
                  {t('common:clear')}
                </Button>
              )}
              <DataTableColumnsSelector table={table} />
            </div>
          </div>

          {/* Total count */}
          <p className="mb-4 shrink-0 text-xs text-muted-foreground">
            {t('authenticated.clients:common.allClients')}: {data?.pagination?.total ?? 0}
          </p>

          {/* Scrollable table */}
          <ClientsTable
            table={table}
            isLoading={isLoading || isFetchingNextPage || isRefreshing}
            hasMore={hasNextPage}
            onLoadMore={() => fetchNextPage()}
            error={error?.message}
            isAnyFilterApplied={Object.values(appliedFilters).some(value => !!value)}
            totalCount={data?.pagination?.total ?? 0}
            handleRowClick={handleRowClick}
            getRowHref={buildRowHref}
            stickyHeader
            fixedHeight
            tableId={TableIds.CLIENTS}
            onResizeEnd={saveColumnSizing}
          />
        </div>
      </PageTemplate>
      {/* MODALS */}
      <DeleteClientsModal
        clientsForDelete={selectedRows}
        onSuccess={handleCloseModal}
        open={modalType === ClientModalType.Delete}
        onClose={handleCloseModal}
      />

      {selectedRows[0] && (
        <ArchiveClientModal
          client={selectedRows[0]}
          open={modalType === ClientModalType.Archive}
          onOpen={handleCloseModal}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
